/**
 * MapLegend Component
 *
 * Phase 5: Map Controls & Legend Implementation
 * Phase 7: Responsive & Accessibility Enhancements
 *
 * Provides an informational legend for the MapComponent:
 * - Gradient color bar showing moisture scale (0-100%)
 * - Tick marks with labels (0%, 25%, 50%, 75%, 100%)
 * - Theme-aware colors (agricultural, thermal, ocean, grayscale)
 * - Collapse/expand toggle (minimize to icon)
 * - Configurable position (all 4 corners)
 * - Sensor count display
 * - Last updated timestamp from useMapSensorData
 * - Render mode toggles (Heatmap / Markers / Clusters)
 * - Zones overlay toggle
 * - Layer visibility checkboxes with search
 *
 * Phase 7 Responsive Design:
 * - Mobile (≤599px): Compact icon-only mode, minimal info
 * - Tablet (600-1023px): Collapsed by default, expandable
 * - Desktop (≥1024px): Fully expanded with all details
 *
 * Phase 7 Accessibility (WCAG 2.1 AA):
 * - Full keyboard navigation (Tab, Enter, Space)
 * - ARIA labels for all interactive elements
 * - Screen reader announcements via aria-live regions
 * - High contrast text (minimum 4.5:1 ratio)
 * - Focus indicators with visible focus rings
 *
 * Created: January 2026
 * Updated: February 2026 - Integrated layer controls from LayerPanel
 */

import React, { useCallback, useState, useEffect, useMemo, useRef } from 'react';
import {
  ChevronDown,
  ChevronUp,
  Layers,
  Clock,
  Wifi,
  WifiOff,
  Radio,
  Search,
  Eye,
  EyeOff,
  X,
  Activity,
  Map as MapIcon,
} from 'lucide-react';
import { Checkbox } from '@/components/ui/checkbox';
import { cn } from '@/lib/utils';
import {
  MapLegendConfig,
  ControlPosition,
  MAP_COLOR_THEMES,
  DEFAULT_LEGEND_CONFIG,
} from '../MapComponent.types';
import type { MapGroup } from '../MapComponent.types';
import { formatLastUpdated, getConnectionStatusStyles } from '../mapLayers/updateIndicators';
import {
  mapControlButtonClass,
  mapControlDividerClass,
  mapControlHeaderClass,
  mapControlInputClass,
  mapControlSubtleButtonClass,
  mapControlSurfaceClass,
  mapControlText,
} from './mapControlStyles';

// ============================================================================
// Types
// ============================================================================

export interface MapLegendProps {
  /** Current color theme */
  colorTheme: string;
  /** Number of sensors displayed */
  sensorCount: number;
  /** Last updated timestamp */
  lastUpdated?: Date | null;
  /** Connection status for real-time updates */
  connectionStatus?: 'connected' | 'disconnected' | 'reconnecting';
  /** Whether real-time is enabled */
  isRealTimeEnabled?: boolean;
  /** Number of recently updated sensors */
  recentlyUpdatedCount?: number;
  /** Legend configuration */
  config?: Partial<MapLegendConfig>;
  /** Container width for responsive sizing */
  containerWidth?: number;
  /** Container height for scaling legend */
  containerHeight?: number;
  /** Additional CSS class */
  className?: string;
  /** Array of map groups (layers) to display */
  groups?: MapGroup[];
  /** Set of currently visible group IDs */
  visibleGroups?: Set<string>;
  /** Callback when a group's visibility changes */
  onGroupVisibilityChange?: (groupId: string, visible: boolean) => void;
  /** Callback to toggle all groups visibility */
  onToggleAll?: (visible: boolean) => void;
  /** Current render mode */
  renderMode?: 'heatmap' | 'markers' | 'clusters';
  /** Callback when render mode changes */
  onRenderModeChange?: (mode: 'heatmap' | 'markers' | 'clusters') => void;
  /** Whether zones overlay is enabled */
  zonesEnabled?: boolean;
  /** Callback when zones toggle changes */
  onZonesToggle?: (enabled: boolean) => void;
}

// ============================================================================
// Breakpoint Constants (from CLAUDE.md)
// ============================================================================

const BREAKPOINTS = {
  mobile: 599,   // ≤599px
  tablet: 1023,  // 600-1023px
  desktop: 1024, // ≥1024px
} as const;

type ViewportSize = 'mobile' | 'tablet' | 'desktop';

// ============================================================================
// Position Styles
// ============================================================================

const getPositionStyles = (position: ControlPosition, isMobile: boolean): React.CSSProperties => {
  const base: React.CSSProperties = {
    position: 'absolute',
    zIndex: 10,
  };

  // Bottom offset needs to clear the map's scale indicator control
  // Scale indicator is at bottom: 35px and ~35px tall, so legend needs bottom: ~80px
  const bottomOffset = isMobile ? 50 : 80;

  // Right offset needs to clear the navigation controls (zoom +/-, etc.)
  // Controls are at right: 10 and ~50px wide, so legend needs right: ~70px
  const rightOffset = isMobile ? 10 : 70;

  // Top offset to ensure legend isn't clipped by container edges
  const topOffset = isMobile ? 10 : 20;

  switch (position) {
    case 'top-left':
      return { ...base, top: topOffset, left: 10 };
    case 'top-right':
      return { ...base, top: topOffset, right: rightOffset };
    case 'bottom-left':
      return { ...base, bottom: bottomOffset, left: 10 };
    case 'bottom-right':
      return { ...base, bottom: bottomOffset, right: rightOffset };
    default:
      return { ...base, bottom: bottomOffset, left: 10 };
  }
};

// ============================================================================
// Accessibility Styles
// ============================================================================

const focusRingStyles = 'focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2';

// High contrast colors for WCAG 2.1 AA compliance (4.5:1 ratio minimum)
const highContrastText = {
  primary: mapControlText.primary,
  secondary: mapControlText.secondary,
  tertiary: mapControlText.tertiary,
  muted: mapControlText.muted,
};

// ============================================================================
// Gradient Component
// ============================================================================

interface MoistureGradientProps {
  colorTheme: string;
  showTickMarks?: boolean;
  compact?: boolean;
}

const MoistureGradient: React.FC<MoistureGradientProps> = ({
  colorTheme,
  showTickMarks = true,
  compact = false,
}) => {
  const colors = MAP_COLOR_THEMES[colorTheme] || MAP_COLOR_THEMES.agricultural;
  // Remove transparent color from gradient
  const gradientColors = colors.slice(1);
  const gradientString = `linear-gradient(to right, ${gradientColors.join(', ')})`;

  return (
    <div className="space-y-1" role="img" aria-label="Soil moisture gradient scale from dry (0%) to wet (100%)">
      <div className="flex items-center gap-1">
        <span
          className={cn('font-medium', compact ? 'text-[9px]' : 'text-[10px]')}
          style={{ color: highContrastText.secondary, width: compact ? '20px' : '24px' }}
          aria-hidden="true"
        >
          Dry
        </span>
        <div
          className={cn('flex-1 rounded', compact ? 'h-2' : 'h-3')}
          style={{ background: gradientString }}
          data-testid="legend-gradient"
          aria-hidden="true"
        />
        <span
          className={cn('font-medium text-right', compact ? 'text-[9px]' : 'text-[10px]')}
          style={{ color: highContrastText.secondary, width: compact ? '20px' : '24px' }}
          aria-hidden="true"
        >
          Wet
        </span>
      </div>
      {showTickMarks && (
        <div
          className={cn('flex justify-between font-medium', compact ? 'px-5 text-[8px]' : 'px-7 text-[9px]')}
          style={{ color: highContrastText.tertiary }}
          aria-hidden="true"
        >
          <span>0%</span>
          <span>25%</span>
          <span>50%</span>
          <span>75%</span>
          <span>100%</span>
        </div>
      )}
    </div>
  );
};

// ============================================================================
// Render Mode Toggle Component
// ============================================================================

interface RenderModeOption {
  value: 'heatmap' | 'markers' | 'clusters';
  label: string;
  icon: React.ReactNode;
}

interface RenderModeToggleProps {
  label: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
  icon?: React.ReactNode;
  compact?: boolean;
}

const RenderModeToggle: React.FC<RenderModeToggleProps> = ({
  label,
  checked,
  onChange,
  icon,
  compact = false,
}) => {
  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onChange(!checked);
    }
  }, [checked, onChange]);

  return (
    <label
      className={cn(
        'flex items-center gap-2 cursor-pointer py-0.5 rounded',
        focusRingStyles,
      )}
      onKeyDown={handleKeyDown}
      tabIndex={0}
      role="radio"
      aria-checked={checked}
      aria-label={`${label}: ${checked ? 'active' : 'inactive'}`}
    >
      <Checkbox
        checked={checked}
        onCheckedChange={(val) => onChange(val === true)}
        className={cn(focusRingStyles, compact ? 'h-3 w-3' : 'h-3.5 w-3.5')}
        data-testid={`render-mode-${label.toLowerCase()}`}
        aria-hidden="true"
        tabIndex={-1}
      />
      {icon && <span aria-hidden="true">{icon}</span>}
      <span
        className={cn('font-medium', compact ? 'text-[10px]' : 'text-[11px]')}
        style={{ color: highContrastText.primary }}
      >
        {label}
      </span>
    </label>
  );
};

// ============================================================================
// Layer Item Component
// ============================================================================

interface LayerItemProps {
  group: MapGroup;
  isVisible: boolean;
  onChange: (visible: boolean) => void;
  compact?: boolean;
}

const LayerItem: React.FC<LayerItemProps> = ({
  group,
  isVisible,
  onChange,
  compact = false,
}) => {
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        onChange(!isVisible);
      }
    },
    [isVisible, onChange]
  );

  return (
    <label
      className={cn(
        'flex items-center gap-2 cursor-pointer py-1 px-1.5 rounded',
        mapControlButtonClass,
        focusRingStyles
      )}
      onKeyDown={handleKeyDown}
      tabIndex={0}
      role="checkbox"
      aria-checked={isVisible}
      aria-label={`${group.name} layer: ${isVisible ? 'visible' : 'hidden'}`}
    >
      <Checkbox
        checked={isVisible}
        onCheckedChange={(checked) => onChange(checked === true)}
        className={cn(focusRingStyles, compact ? 'h-3 w-3' : 'h-3.5 w-3.5')}
        data-testid={`layer-toggle-${group.id}`}
        aria-hidden="true"
        tabIndex={-1}
      />
      <div
        className={cn('rounded-full', compact ? 'w-2.5 h-2.5' : 'w-3 h-3')}
        style={{ backgroundColor: group.color }}
        aria-hidden="true"
      />
      <span
        className={cn(
          'font-medium truncate flex-1',
          compact ? 'text-[10px]' : 'text-[11px]'
        )}
        style={{ color: highContrastText.primary }}
        title={group.name}
      >
        {group.name}
      </span>
      {group.type === 'area' && (
        <span
          className={cn(
            'text-muted-foreground',
            compact ? 'text-[8px]' : 'text-[9px]'
          )}
          title="Area layer with polygon boundary"
        >
          &#x25A2;
        </span>
      )}
    </label>
  );
};

// ============================================================================
// Main Component
// ============================================================================

export const MapLegend: React.FC<MapLegendProps> = ({
  colorTheme,
  sensorCount,
  lastUpdated,
  connectionStatus = 'disconnected',
  isRealTimeEnabled = false,
  recentlyUpdatedCount = 0,
  config = {},
  containerWidth,
  containerHeight,
  className,
  groups = [],
  visibleGroups,
  onGroupVisibilityChange,
  onToggleAll,
  renderMode = 'heatmap',
  onRenderModeChange,
  zonesEnabled = false,
  onZonesToggle,
}) => {
  // Merge config with defaults
  const mergedConfig: MapLegendConfig = {
    ...DEFAULT_LEGEND_CONFIG,
    ...config,
  };

  // Refs
  const announceRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  // State
  const [lastAnnouncement, setLastAnnouncement] = useState('');
  const [searchQuery, setSearchQuery] = useState('');

  // Determine viewport size
  const getViewportSize = useCallback((): ViewportSize => {
    if (containerWidth === undefined) return 'desktop';
    if (containerWidth <= BREAKPOINTS.mobile) return 'mobile';
    if (containerWidth <= BREAKPOINTS.tablet) return 'tablet';
    return 'desktop';
  }, [containerWidth]);

  const viewportSize = getViewportSize();

  // Calculate scale factor based on container height
  // Scale down when height is below 300px (base height)
  const BASE_HEIGHT = 300;
  const MIN_SCALE = 0.5;
  const heightScale = containerHeight !== undefined && containerHeight < BASE_HEIGHT
    ? Math.max(MIN_SCALE, containerHeight / BASE_HEIGHT)
    : 1;

  const isMobile = viewportSize === 'mobile';
  const isTablet = viewportSize === 'tablet';
  const isCompact = isMobile || isTablet || heightScale < 0.8;

  // Auto-collapse on mobile/tablet
  const [isExpanded, setIsExpanded] = useState(() => {
    if (isMobile) return false;
    if (isTablet) return false;
    return mergedConfig.expanded;
  });

  // Update expanded state when viewport changes
  useEffect(() => {
    if (isMobile && isExpanded) {
      console.log('[MapLegend] Auto-collapsing for mobile viewport');
      setIsExpanded(false);
    }
  }, [viewportSize, isMobile, isExpanded]);

  // ---------------------------------------------------------------------------
  // Layer Control Logic
  // ---------------------------------------------------------------------------

  const totalCount = groups.length;
  const visibleCount = visibleGroups?.size ?? 0;
  const allVisible = totalCount > 0 && visibleCount === totalCount;
  const noneVisible = visibleCount === 0;
  const showSearch = totalCount > 5;

  const filteredGroups = useMemo(() => {
    if (!searchQuery.trim()) return groups;
    const query = searchQuery.toLowerCase();
    return groups.filter(
      (g) =>
        g.name.toLowerCase().includes(query) ||
        g.description?.toLowerCase().includes(query)
    );
  }, [groups, searchQuery]);

  const renderModeOptions: RenderModeOption[] = useMemo(() => [
    {
      value: 'heatmap',
      label: 'Heatmap',
      icon: <Activity className={cn('text-orange-600', isCompact ? 'w-2.5 h-2.5' : 'w-3 h-3')} />,
    },
    {
      value: 'markers',
      label: 'Markers',
      icon: <MapIcon className={cn('text-primary', isCompact ? 'w-2.5 h-2.5' : 'w-3 h-3')} />,
    },
    {
      value: 'clusters',
      label: 'Clusters',
      icon: <Layers className={cn('text-purple-600', isCompact ? 'w-2.5 h-2.5' : 'w-3 h-3')} />,
    },
  ], [isCompact]);

  // ---------------------------------------------------------------------------
  // Screen Reader Announcement Helper
  // ---------------------------------------------------------------------------

  const announce = useCallback((message: string) => {
    setLastAnnouncement(message);
    console.log('[MapLegend] Screen reader announcement:', message);
    setTimeout(() => setLastAnnouncement(''), 1000);
  }, []);

  // ---------------------------------------------------------------------------
  // Handlers
  // ---------------------------------------------------------------------------

  const handleToggleExpand = useCallback(() => {
    const newExpanded = !isExpanded;
    console.log('[MapLegend] Legend toggled:', newExpanded ? 'expanded' : 'collapsed');
    setIsExpanded(newExpanded);
    announce(newExpanded ? 'Legend expanded' : 'Legend collapsed');
  }, [isExpanded, announce]);

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleToggleExpand();
    }
  }, [handleToggleExpand]);

  const handleRenderModeSelect = useCallback(
    (mode: 'heatmap' | 'markers' | 'clusters') => {
      console.log('[MapLegend] Render mode changed to:', mode);
      onRenderModeChange?.(mode);
      announce(`Render mode changed to ${mode}`);
    },
    [onRenderModeChange, announce]
  );

  const handleZonesToggle = useCallback(
    (enabled: boolean) => {
      console.log('[MapLegend] Zones toggled:', enabled);
      onZonesToggle?.(enabled);
      announce(`Zones overlay ${enabled ? 'enabled' : 'disabled'}`);
    },
    [onZonesToggle, announce]
  );

  const handleGroupToggle = useCallback(
    (groupId: string, visible: boolean) => {
      console.log('[MapLegend] Layer visibility changed:', { groupId, visible });
      onGroupVisibilityChange?.(groupId, visible);
      const group = groups.find((g) => g.id === groupId);
      if (group) {
        announce(`${group.name} ${visible ? 'shown' : 'hidden'}`);
      }
    },
    [groups, onGroupVisibilityChange, announce]
  );

  const handleShowAll = useCallback(() => {
    console.log('[MapLegend] Show all layers');
    onToggleAll?.(true);
    announce('All layers shown');
  }, [onToggleAll, announce]);

  const handleHideAll = useCallback(() => {
    console.log('[MapLegend] Hide all layers');
    onToggleAll?.(false);
    announce('All layers hidden');
  }, [onToggleAll, announce]);

  const handleClearSearch = useCallback(() => {
    setSearchQuery('');
    searchInputRef.current?.focus();
  }, []);

  // ---------------------------------------------------------------------------
  // Collapsed View
  // ---------------------------------------------------------------------------

  if (!isExpanded) {
    return (
      <>
        {/* Screen reader live region */}
        <div
          ref={announceRef}
          aria-live="polite"
          aria-atomic="true"
          className="sr-only"
          role="status"
        >
          {lastAnnouncement}
        </div>

        <div
          className={cn(
            'flex items-center gap-1 cursor-pointer',
            mapControlSurfaceClass,
            'transition-all duration-200 ease-in-out',
            focusRingStyles,
            isMobile ? 'p-1' : 'p-1.5',
            className
          )}
          style={{
            ...getPositionStyles(mergedConfig.position, isMobile),
            transform: heightScale < 1 ? `scale(${heightScale})` : undefined,
            transformOrigin: `${mergedConfig.position.includes('bottom') ? 'bottom' : 'top'} ${mergedConfig.position.includes('right') ? 'right' : 'left'}`,
          }}
          onClick={handleToggleExpand}
          onKeyDown={handleKeyDown}
          data-testid="map-legend"
          role="button"
          aria-label={`Map legend, collapsed. ${sensorCount} sensors${totalCount > 0 ? `, ${visibleCount}/${totalCount} layers` : ''}. Press Enter to expand.`}
          aria-expanded={false}
          tabIndex={0}
        >
          <Layers
            className={cn(isMobile ? 'w-3.5 h-3.5' : 'w-4 h-4')}
            style={{ color: highContrastText.secondary }}
            aria-hidden="true"
          />
          {!isMobile && (
            <span
              className="text-[10px] font-medium"
              style={{ color: highContrastText.primary }}
            >
              {sensorCount}
              {totalCount > 0 && (
                <span style={{ color: highContrastText.muted }}>{' '}{visibleCount}/{totalCount}</span>
              )}
            </span>
          )}
          <ChevronUp
            className={cn(isMobile ? 'w-2.5 h-2.5' : 'w-3 h-3')}
            style={{ color: highContrastText.secondary }}
            aria-hidden="true"
          />
        </div>
      </>
    );
  }

  // ---------------------------------------------------------------------------
  // Expanded View
  // ---------------------------------------------------------------------------

  const connectionStyles = getConnectionStatusStyles(connectionStatus);

  return (
    <>
      {/* Screen reader live region */}
      <div
        ref={announceRef}
        aria-live="polite"
        aria-atomic="true"
        className="sr-only"
        role="status"
      >
        {lastAnnouncement}
      </div>

      <div
        className={cn(
          mapControlSurfaceClass,
          'transition-all duration-200 ease-in-out',
          'flex flex-col',
          className
        )}
        style={{
          ...getPositionStyles(mergedConfig.position, isMobile),
          minWidth: isCompact ? 140 : 160,
          maxWidth: isCompact ? 190 : 240,
          // Constrain height to fit within container (account for offsets: 80px bottom + 20px top margin)
          maxHeight: containerHeight ? Math.max(150, containerHeight - 120) : undefined,
          transform: heightScale < 1 ? `scale(${heightScale})` : undefined,
          transformOrigin: `${mergedConfig.position.includes('bottom') ? 'bottom' : 'top'} ${mergedConfig.position.includes('right') ? 'right' : 'left'}`,
        }}
        data-testid="map-legend"
        role="region"
        aria-label="Map legend"
      >
        {/* Header */}
        <div
          className={cn(
            'flex items-center justify-between cursor-pointer',
            mapControlHeaderClass,
            focusRingStyles,
            isCompact ? 'px-1.5 py-1' : 'px-2 py-1.5'
          )}
          onClick={handleToggleExpand}
          onKeyDown={handleKeyDown}
          role="button"
          aria-label="Collapse legend"
          aria-expanded={true}
          tabIndex={0}
        >
          <div className="flex items-center gap-1.5">
            <Layers
              className={cn(isCompact ? 'w-3 h-3' : 'w-3.5 h-3.5')}
              style={{ color: highContrastText.secondary }}
              aria-hidden="true"
            />
            <span
              className={cn('font-semibold', isCompact ? 'text-[10px]' : 'text-xs')}
              style={{ color: highContrastText.primary }}
            >
              Legend
            </span>
          </div>
          <ChevronDown
            className={cn(isCompact ? 'w-2.5 h-2.5' : 'w-3 h-3')}
            style={{ color: highContrastText.secondary }}
            aria-hidden="true"
          />
        </div>

        {/* Content - scrollable when legend exceeds maxHeight */}
        <div className={cn('space-y-2 overflow-y-auto flex-1 min-h-0', isCompact ? 'p-1.5' : 'p-2')}>
          {/* Moisture Scale (opt-in via config) */}
          {mergedConfig.showMoistureGradient && (
            <div>
              <div
                className={cn(
                  'font-semibold uppercase tracking-wide mb-1',
                  isCompact ? 'text-[8px]' : 'text-[10px]'
                )}
                style={{ color: highContrastText.tertiary }}
                id="moisture-scale-label"
              >
                Soil Moisture
              </div>
              <MoistureGradient
                colorTheme={colorTheme}
                showTickMarks={!isMobile}
                compact={isCompact}
              />
            </div>
          )}

          {/* Sensor Count */}
          {mergedConfig.showSensorCount && (
            <div
              className={cn(
                'flex items-center justify-between pt-1.5 border-t font-medium',
                mapControlDividerClass,
                isCompact ? 'text-[9px]' : 'text-[10px]'
              )}
              role="status"
              aria-label={`${sensorCount} sensors displayed`}
            >
              <span style={{ color: highContrastText.tertiary }}>Sensors:</span>
              <span style={{ color: highContrastText.primary }}>{sensorCount}</span>
            </div>
          )}

          {/* Real-time Status */}
          {isRealTimeEnabled && !isMobile && (
            <div
              className={cn("space-y-1 pt-1.5 border-t", mapControlDividerClass)}
              role="status"
              aria-label={`Connection status: ${connectionStatus}. ${recentlyUpdatedCount} sensors updating.`}
            >
              <div className="flex items-center gap-1.5">
                <div
                  className="w-2 h-2 rounded-full animate-pulse"
                  style={{ backgroundColor: connectionStyles.dotColor }}
                  aria-hidden="true"
                />
                {connectionStatus === 'connected' ? (
                  <Wifi className="w-3 h-3" style={{ color: connectionStyles.textColor }} aria-hidden="true" />
                ) : connectionStatus === 'reconnecting' ? (
                  <Radio className="w-3 h-3 animate-pulse" style={{ color: connectionStyles.textColor }} aria-hidden="true" />
                ) : (
                  <WifiOff className="w-3 h-3" style={{ color: connectionStyles.textColor }} aria-hidden="true" />
                )}
                <span
                  className={cn('font-medium', isCompact ? 'text-[9px]' : 'text-[10px]')}
                  style={{ color: connectionStyles.textColor }}
                >
                  {connectionStyles.label}
                </span>
              </div>
              {recentlyUpdatedCount > 0 && (
                <div
                  className={cn('text-primary font-medium', isCompact ? 'text-[9px]' : 'text-[10px]')}
                >
                  {recentlyUpdatedCount} sensor{recentlyUpdatedCount !== 1 ? 's' : ''} updating
                </div>
              )}
            </div>
          )}

          {/* Last Updated */}
          {mergedConfig.showLastUpdated && lastUpdated && !isMobile && (
            <div
              className={cn(
                'flex items-center gap-1 pt-1.5 border-t',
                mapControlDividerClass,
                isCompact ? 'text-[9px]' : 'text-[10px]'
              )}
              role="status"
              aria-label={`Last updated: ${formatLastUpdated(lastUpdated)}`}
            >
              <Clock
                className={cn(isCompact ? 'w-2.5 h-2.5' : 'w-3 h-3')}
                style={{ color: highContrastText.tertiary }}
                aria-hidden="true"
              />
              <span style={{ color: highContrastText.tertiary }}>
                Updated: <span className="font-medium" style={{ color: highContrastText.secondary }}>{formatLastUpdated(lastUpdated)}</span>
              </span>
            </div>
          )}

          {/* ================================================================= */}
          {/* Layer Controls Section */}
          {/* ================================================================= */}

          {/* Render Mode Toggles */}
          {onRenderModeChange && (
            <div className={cn("border-t pt-1.5 mt-1.5", mapControlDividerClass)} role="radiogroup" aria-labelledby="render-mode-label">
              <div
                className={cn(
                  'font-semibold uppercase tracking-wide mb-1',
                  isCompact ? 'text-[8px]' : 'text-[10px]'
                )}
                style={{ color: highContrastText.tertiary }}
                id="render-mode-label"
              >
                Render Mode
              </div>
              <div className="space-y-0.5">
                {renderModeOptions.map((opt) => (
                  <RenderModeToggle
                    key={opt.value}
                    label={opt.label}
                    checked={renderMode === opt.value}
                    onChange={(checked) => {
                      if (checked) handleRenderModeSelect(opt.value);
                    }}
                    icon={opt.icon}
                    compact={isCompact}
                  />
                ))}
              </div>

              {/* Zones Toggle (independent overlay, not a render mode) */}
              {onZonesToggle && !isMobile && (
                <div className="mt-1">
                  <RenderModeToggle
                    label="Zones"
                    checked={zonesEnabled}
                    onChange={handleZonesToggle}
                    icon={<MapIcon className={cn('text-green-600', isCompact ? 'w-2.5 h-2.5' : 'w-3 h-3')} />}
                    compact={isCompact}
                  />
                </div>
              )}
            </div>
          )}

          {/* Layer Visibility List */}
          {totalCount > 0 && (
            <div className={cn(onRenderModeChange ? 'border-t pt-1.5' : 'border-t pt-1.5 mt-1.5', mapControlDividerClass)}>
              {/* Toggle All Buttons */}
              <div
                className={cn(
                  'flex gap-1 mb-1.5',
                  isCompact ? 'text-[9px]' : 'text-[10px]'
                )}
              >
                <button
                  onClick={handleShowAll}
                  disabled={allVisible}
                  className={cn(
                    'flex items-center gap-1 px-1.5 py-0.5 rounded',
                    'transition-colors',
                    focusRingStyles,
                    allVisible
                      ? 'bg-muted/50 text-muted-foreground/60 cursor-not-allowed'
                      : 'border border-primary/30 bg-primary/10 text-primary hover:bg-primary/15'
                  )}
                  aria-label="Show all layers"
                  data-testid="layer-show-all"
                >
                  <Eye className={cn(isCompact ? 'w-2.5 h-2.5' : 'w-3 h-3')} />
                  <span>Show All</span>
                </button>
                <button
                  onClick={handleHideAll}
                  disabled={noneVisible}
                  className={cn(
                    'flex items-center gap-1 px-1.5 py-0.5 rounded',
                    'transition-colors',
                    focusRingStyles,
                    noneVisible
                      ? 'bg-muted/50 text-muted-foreground/60 cursor-not-allowed'
                      : mapControlSubtleButtonClass
                  )}
                  aria-label="Hide all layers"
                  data-testid="layer-hide-all"
                >
                  <EyeOff className={cn(isCompact ? 'w-2.5 h-2.5' : 'w-3 h-3')} />
                  <span>Hide All</span>
                </button>
              </div>

              {/* Search Input */}
              {showSearch && (
                <div className="relative mb-1.5">
                  <Search
                    className={cn(
                      'absolute left-1.5 top-1/2 -translate-y-1/2 text-muted-foreground',
                      isCompact ? 'w-2.5 h-2.5' : 'w-3 h-3'
                    )}
                    aria-hidden="true"
                  />
                  <input
                    ref={searchInputRef}
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Search layers..."
                    className={cn(
                      'w-full pl-5 pr-5 py-1 rounded',
                      mapControlInputClass,
                      focusRingStyles,
                      isCompact ? 'text-[9px]' : 'text-[10px]'
                    )}
                    aria-label="Search layers"
                    data-testid="layer-search-input"
                  />
                  {searchQuery && (
                    <button
                      onClick={handleClearSearch}
                      className={cn(
                        'absolute right-1 top-1/2 -translate-y-1/2 p-0.5 rounded',
                        'text-muted-foreground hover:text-foreground',
                        focusRingStyles
                      )}
                      aria-label="Clear search"
                    >
                      <X className={cn(isCompact ? 'w-2.5 h-2.5' : 'w-3 h-3')} />
                    </button>
                  )}
                </div>
              )}

              {/* Layer List */}
              <div
                className="flex-1 overflow-y-auto min-h-0"
                role="group"
                aria-labelledby="layer-list-label"
              >
                <div id="layer-list-label" className="sr-only">
                  Layer visibility toggles
                </div>
                {filteredGroups.length === 0 ? (
                  <div
                    className={cn(
                      'text-center py-2',
                      isCompact ? 'text-[9px]' : 'text-[10px]'
                    )}
                    style={{ color: highContrastText.muted }}
                  >
                    No layers match &quot;{searchQuery}&quot;
                  </div>
                ) : (
                  <div className="space-y-0.5">
                    {filteredGroups.map((group) => (
                      <LayerItem
                        key={group.id}
                        group={group}
                        isVisible={visibleGroups?.has(group.id) ?? false}
                        onChange={(visible) => handleGroupToggle(group.id, visible)}
                        compact={isCompact}
                      />
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Screen reader only: Full legend description */}
        <div className="sr-only" role="note">
          Map legend showing soil moisture levels from dry (0%) to wet (100%) using a color gradient.
          Currently displaying {sensorCount} sensors.
          {isRealTimeEnabled && ` Connection status: ${connectionStatus}.`}
          {lastUpdated && ` Last updated ${formatLastUpdated(lastUpdated)}.`}
          {totalCount > 0 && ` ${totalCount} layers available, ${visibleCount} visible.`}
        </div>
      </div>
    </>
  );
};

export default MapLegend;
